/**
 * OSTQuickCreate
 *
 * Creates a new Opportunity Solution Tree linked to an existing canonical Strategy,
 * seeds a root Strategy (outcome) node from that strategy’s name and description,
 * then opens the tree editor. Requires `?strategyId=` from the create-strategy flow.
 *
 * Route: /prioritization/strategy/opportunity-trees/new?strategyId=...
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useOSTStore } from '../../../stores/prioritization/ostStore';
import { useStrategyStore } from '../../../stores/prioritization/strategyStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';

async function ensureStrategyRootNode(
  treeId: string,
  strategyId: string,
  fallbackName: string,
  fallbackDescription: string | undefined,
  createdBy: string | undefined
): Promise<boolean> {
  const { fetchNodes, createNode, getNodesForTree } = useOSTStore.getState();
  await fetchNodes(treeId);
  const nodes = getNodesForTree(treeId);
  if (nodes.some((n) => n.nodeType === 'outcome')) return true;

  await useStrategyStore.getState().fetchFromSupabase();
  const canonical = useStrategyStore.getState().getStrategy(strategyId);
  const title = canonical?.name?.trim() || fallbackName || 'Strategy';
  const description = canonical?.description?.trim() || fallbackDescription || '';

  const created = await createNode({
    treeId,
    nodeType: 'outcome',
    title,
    description: description || undefined,
    createdBy,
  });
  return created != null;
}

export function OSTQuickCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const strategyId = searchParams.get('strategyId');

  const createTree = useOSTStore((s) => s.createTree);
  const fetchTrees = useOSTStore((s) => s.fetchTrees);
  const workspaceId = getCurrentWorkspaceId();
  const userId = useUserStore((s) => s.userId);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!strategyId?.trim()) {
      navigate('/prioritization/strategy/new?framework=ost', { replace: true });
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        await useStrategyStore.getState().fetchFromSupabase();
        if (cancelled) return;

        await fetchTrees(workspaceId || undefined);
        if (cancelled) return;

        const existing = useOSTStore
          .getState()
          .trees.find((t) => t.strategyId === strategyId);

        const canonical = useStrategyStore.getState().getStrategy(strategyId);
        const name = canonical?.name?.trim() || 'Opportunity Solution Tree';
        const description = canonical?.description?.trim() || '';

        if (existing) {
          const ok = await ensureStrategyRootNode(
            existing.id,
            strategyId,
            existing.name,
            existing.description,
            userId || undefined
          );
          if (cancelled) return;
          if (!ok) {
            setError('Failed to prepare tree. Please try again.');
            return;
          }
          navigate(`/prioritization/strategy/opportunity-tree/${existing.id}`, { replace: true });
          return;
        }

        const tree = await createTree({
          name,
          description: description || undefined,
          workspaceId: workspaceId ?? undefined,
          strategyId,
        });
        if (cancelled) return;

        if (!tree) {
          setError('Failed to create tree. Please try again.');
          return;
        }

        const seeded = await ensureStrategyRootNode(
          tree.id,
          strategyId,
          name,
          description,
          userId || undefined
        );
        if (cancelled) return;
        if (!seeded) {
          setError('Failed to create the strategy node. Please try again.');
          return;
        }

        navigate(`/prioritization/strategy/opportunity-tree/${tree.id}`, { replace: true });
      } catch {
        if (!cancelled) setError('Failed to create tree. Please try again.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [createTree, fetchTrees, workspaceId, navigate, strategyId, userId]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
      {error ? (
        <div className="text-center space-y-3">
          <p className="text-sm text-[var(--coral)]">{error}</p>
          <button
            type="button"
            onClick={() => navigate('/prioritization/strategy', { replace: true })}
            className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)] underline"
          >
            Back to Strategy
          </button>
        </div>
      ) : (
        <>
          <span className="h-7 w-7 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-[var(--text-ter)]">Creating your tree…</p>
        </>
      )}
    </div>
  );
}

export default OSTQuickCreate;
